/**
 * Listings API Client
 *
 * Provides typed API methods for searching and fetching listings.
 * Uses config-driven transformers for maintainability and DRY code.
 */

import { apiClient } from "../apiClient";
import type {
  ListingsSearchParams,
  ListingsSearchResponse,
  Listing,
} from "../../types/listings";
import {
  transformListing,
  transformListings,
  buildListingsQueryString,
  type ApiListingData,
} from "../transformers/listings";

// ============================================
// API Response Types
// ============================================

/**
 * Raw API response for listings search
 */
interface ApiListingsResponse {
  listings: ApiListingData[];
  total_count: number;
  next_cursor?: string | null;
  has_more: boolean;
}

/**
 * Raw API response for single listing
 */
interface ApiListingResponse {
  listing: ApiListingData;
}

// ============================================
// API Functions
// ============================================

/**
 * Search listings with filters
 *
 * @param params - Search parameters (optional)
 * @returns Transformed search results with listings, count, and pagination info
 *
 * @example
 * ```ts
 * const results = await searchListings({
 *   prefecture: "tokyo",
 *   rentMin: 50000,
 *   rentMax: 100000,
 *   airConditioning: true
 * });
 * ```
 */
export async function searchListings(
  params: ListingsSearchParams = {},
): Promise<ListingsSearchResponse> {
  const queryString = buildListingsQueryString(params);
  const endpoint = queryString ? `/listings?${queryString}` : "/listings";

  const response = await apiClient.get<ApiListingsResponse>(endpoint);

  return {
    listings: transformListings(response.listings),
    totalCount: response.total_count,
    nextCursor: response.next_cursor ?? undefined,
    hasMore: response.has_more,
  };
}

/**
 * Get a single listing by ID
 *
 * @param id - Listing ID
 * @returns Transformed listing data
 * @throws Error if listing not found
 *
 * @example
 * ```ts
 * const listing = await getListing("listing-123");
 * console.log(listing.title, listing.price);
 * ```
 */
export async function getListing(id: string): Promise<Listing> {
  const encodedId = encodeURIComponent(id);
  const response = await apiClient.get<ApiListingResponse>(
    `/listings/${encodedId}`,
  );

  return transformListing(response.listing);
}

/**
 * Load more listings using cursor-based pagination
 *
 * @param params - Current search parameters
 * @param cursor - Pagination cursor from previous response
 * @returns Transformed search results for next page
 *
 * @example
 * ```ts
 * const firstPage = await searchListings({ prefecture: "tokyo" });
 * if (firstPage.hasMore && firstPage.nextCursor) {
 *   const nextPage = await loadMoreListings(
 *     { prefecture: "tokyo" },
 *     firstPage.nextCursor
 *   );
 * }
 * ```
 */
export async function loadMoreListings(
  params: ListingsSearchParams,
  cursor: string,
): Promise<ListingsSearchResponse> {
  return searchListings({ ...params, cursor });
}
